// mds-dojo — dijit/layout/_LayoutWidget — FAITHFUL port of
// references/dijit/layout/_LayoutWidget.js. AMD define([...]) → ESM + classic
// declare() from the faithful C3 runtime. `../_Widget` maps to dijit/_Widget,
// `../Viewport` → the local ./_Viewport shim, and domGeometry's box helpers come
// from ./_geometry (dojo/dom-geometry only ships getMarginBox).
import lang from '../../dojo/_base/lang';
import _Widget from '../_Widget';
import _Container from '../_Container';
import _Contained from '../_Contained';
import Viewport from '../Viewport';
import declare from '../../dojo/_base/declare-runtime';
import domClass from '../../dojo/dom-class';
import domGeometry from '../../dojo/dom-geometry';
import domStyle from '../../dojo/dom-style';

export default declare('dijit.layout._LayoutWidget', [_Widget, _Container, _Contained], {
  // summary:
  //		Base class for a _Container widget which is responsible for laying out its children.

  // baseClass: [protected extension] String
  baseClass: 'dijitLayoutContainer',

  // isLayoutContainer: [protected] Boolean
  isLayoutContainer: true,

  // Cancel _WidgetBase's _setTitleAttr so the title (tab label) isn't set as an
  // attribute on this.domNode (otherwise a tooltip shows over the whole widget).
  _setTitleAttr: null,

  buildRendering: function () {
    this.inherited(arguments);
    domClass.add(this.domNode, 'dijitContainer');
  },

  startup: function () {
    // Called after all widgets have been instantiated and inserted into the DOM.
    if (this._started) { return; }

    // Need to call inherited first - so that child widgets get started up correctly
    this.inherited(arguments);

    // If I am not being controlled by a parent layout widget...
    var parent = this.getParent && this.getParent();
    if (!(parent && parent.isLayoutContainer)) {
      // Do recursive sizing and layout of all my descendants
      this.resize();

      // Since my parent isn't a layout container, monitor viewport size changes
      // so that I can re-layout.
      this.own(Viewport.on('resize', lang.hitch(this, 'resize')));
    }
  },

  resize: function (changeSize: any, resultSize: any) {
    // Call this to resize a widget, or after its size has changed.
    var node = this.domNode;

    // set margin box size, unless it wasn't specified, in which case use current size
    if (changeSize) {
      domGeometry.setMarginBox(node, changeSize);
    }

    // If either height or width wasn't specified by the user, then query node for it.
    var mb = resultSize || {};
    lang.mixin(mb, changeSize || {});	// changeSize overrides resultSize
    if (!('h' in mb) || !('w' in mb)) {
      mb = lang.mixin(domGeometry.getMarginBox(node), mb);	// fill in missing values
    }

    // Compute and save the size of my border box and content box
    var cs = domStyle.getComputedStyle(node);
    var me = domGeometry.getMarginExtents(node, cs);
    var be = domGeometry.getBorderExtents(node, cs);
    var bb = (this._borderBox = {
      w: mb.w - (me.w + be.w),
      h: mb.h - (me.h + be.h)
    });
    var pe = domGeometry.getPadExtents(node, cs);
    this._contentBox = {
      l: domStyle.toPixelValue(node, cs.paddingLeft),
      t: domStyle.toPixelValue(node, cs.paddingTop),
      w: bb.w - pe.w,
      h: bb.h - pe.h
    };

    // Callback for widget to adjust size of its children
    this.layout();
  },

  layout: function () {
    // Widgets override this method to size and position their contents/children.
  },

  _setupChild: function (child: any) {
    // Common setup for initial children and children added after startup
    var cls = this.baseClass + '-child '
      + (child.baseClass ? this.baseClass + '-' + child.baseClass : '');
    domClass.add(child.domNode, cls);
  },

  addChild: function (child: any, insertIndex: any) {
    // Overrides _Container.addChild() to call _setupChild()
    this.inherited(arguments);
    if (this._started) {
      this._setupChild(child);
    }
  },

  removeChild: function (child: any) {
    // Overrides _Container.removeChild() to remove class added by _setupChild()
    var cls = this.baseClass + '-child'
      + (child.baseClass ?
        ' ' + this.baseClass + '-' + child.baseClass : '');
    domClass.remove(child.domNode, cls);

    this.inherited(arguments);
  }
});
